import { useState } from 'react'
import { systemNote, translate, type Lang } from './i18n'
import { AgencyConfig } from './AgencyConfig'
import { AgencyRules, type FormationPolicyChange } from './AgencyRules'
import { AgencyTheme } from './AgencyTheme'
import { Tabs } from './Tabs'
import { DataNote } from './DataNote'
import type {
  District, DistrictImport, ShiftTemplate,
  ApprovalPolicyView, AuditEntry, DeadLetterItem, DeadLetterSummary, FormationPolicyView, GeoLayerVersion, DeviationPolicyView, Device, Jurisdiction, LicenseView,
  StaffingTarget, StorageView, Branding, BrandingChange,
  Denial,
} from './api'
import { fmtDateTime } from './time'

/**
 * El área del Platform/Security Admin (§16.3).
 *
 * Administra lo que PROTECT sí es dueño —los teléfonos— y deja leer la bitácora. Usuarios y roles
 * no están aquí: viven en el proveedor de identidad de la agencia, y una segunda lista en PROTECT
 * sería una que alguien tendría que acordarse de cerrar el día que un oficial deja el cuerpo.
 */
function DeadLetterRow({ lang, item, busy, onDiscard }: {
  lang: Lang
  item: DeadLetterItem
  busy: boolean
  onDiscard?: (id: string, reason: string) => Promise<boolean>
}) {
  const [motivo, setMotivo] = useState('')
  return (
    <div className="card">
      <div className="row-line">
        <strong className="grow">{item.messageType}</strong>
        <span className="meta">{item.sentAt ? fmtDateTime(item.sentAt, lang) : '—'}</span>
      </div>
      <div className="meta">{item.exceptionType}: {item.exceptionMessage}</div>
      {onDiscard && (
        <div className="row-line">
          <input value={motivo} onChange={(e) => setMotivo(e.target.value)} style={{ flex: '1 1 220px' }}
            placeholder={translate(lang, 'admin.deadLetterReason')} aria-label={translate(lang, 'admin.deadLetterReason')} />
          <button disabled={busy || !motivo.trim()} onClick={() => void onDiscard(item.id, motivo.trim())}>
            {translate(lang, 'admin.deadLetterDiscard')}
          </button>
        </div>
      )}
    </div>
  )
}

export function AdminScreen({
  lang, devices, audit, license, provider, demo, storage, mfa = null, deadLetters = null, deadLetterItems = [], onDiscardDeadLetter, busy, canDevices, canAudit, onRegister, onRevoke,
  auditKind, onAuditKind,
  canConfigure, canReadAgency = canConfigure, canRules = canConfigure, canLayers = canConfigure,
  targets, jurisdictions, onSaveTargets, onSaveJurisdictions,
  deviation, approval, onSaveDeviation, onSaveApproval, loaded, problem, shiftTemplates, onSaveShiftTemplates,
  formation = null, onSaveFormation,
  districts, onUploadDistricts, onDistrictsLoaded,
  zones, onUploadZones, onZonesLoaded, districtVersions, zoneVersions,
  branding = null, onSaveBranding, onSaveBadge, onRemoveBadge,
}: {
  lang: Lang
  devices: Device[]
  audit: AuditEntry[]
  license: LicenseView | null
  provider: string | null
  demo: boolean
  storage: StorageView
  /** Si se exige segundo factor (H-67). */
  mfa?: 'required' | 'provider' | 'off' | null
  /** Mensajes sin procesar (hallazgo H-15). Nulo si quien mira no tiene el registro. */
  deadLetters?: DeadLetterSummary | null
  /** Cada mensaje muerto (H-73). */
  deadLetterItems?: DeadLetterItem[]
  /** Descartar uno ya revisado, con motivo. Solo quien administra la plataforma. */
  onDiscardDeadLetter?: (id: string, reason: string) => Promise<boolean>
  busy: boolean
  canDevices: boolean
  canAudit: boolean
  onRegister: (deviceId: string, label: string) => void
  onRevoke: (deviceId: string, reason: string) => void
  auditKind: string
  onAuditKind: (kind: string) => void
  /** El tema de la agencia: la plataforma (H-65). */
  canConfigure: boolean
  /** Ver la configuración de la agencia: quien cambia una parte la ve entera. */
  canReadAgency?: boolean
  /** Las reglas operativas: el jefe (§7.1, H-65). */
  canRules?: boolean
  /** Distritos y zonas: el analista. */
  canLayers?: boolean
  targets: StaffingTarget[]
  jurisdictions: Jurisdiction[]
  onSaveTargets: (targets: StaffingTarget[]) => void
  onSaveJurisdictions: (map: Jurisdiction[]) => void
  deviation: DeviationPolicyView | null
  approval: ApprovalPolicyView | null
  formation?: FormationPolicyView | null
  onSaveFormation?: (p: FormationPolicyChange) => void
  onSaveDeviation: (p: {
    lateArrivalMinutes: number; minPresenceRatio: number; geofenceMeters: number; reason: string
  }) => void
  onSaveApproval: (p: {
    requiresApproval: boolean; requireDifferentApprover: boolean; reason: string
  }) => void
  loaded: boolean
  problem: Denial | null
  shiftTemplates: ShiftTemplate[]
  onSaveShiftTemplates: (templates: ShiftTemplate[]) => Promise<boolean>
  districts: District[]
  onUploadDistricts: (geoJson: string, validFrom?: string) => Promise<DistrictImport>
  districtVersions?: () => Promise<GeoLayerVersion[]>
  zoneVersions?: () => Promise<GeoLayerVersion[]>
  onDistrictsLoaded: () => void
  zones: District[]
  onUploadZones: (geoJson: string, validFrom?: string) => Promise<DistrictImport>
  onZonesLoaded: () => void
  /** El tema de la agencia (§16.3d). Sin los manejadores, la pestaña no aparece. */
  branding?: Branding | null
  onSaveBranding?: (change: BrandingChange) => Promise<boolean>
  onSaveBadge?: (image: File) => Promise<boolean>
  onRemoveBadge?: () => Promise<boolean>
}) {
  const [nuevoId, setNuevoId] = useState('')
  const [nuevaEtiqueta, setNuevaEtiqueta] = useState('')
  const [motivos, setMotivos] = useState<Record<string, string>>({})

  const cuando = (iso: string) => fmtDateTime(iso, lang)

  return (
    <div className="layout single">
      <aside className="side full">
        <p className="notice">{translate(lang, 'admin.help')}</p>

        {/*
          Cuatro cosas distintas que antes venían apiladas: cómo está montado el despliegue, cómo
          está organizada la agencia, los teléfonos y la bitácora. Quien entra a revocar un
          teléfono no tiene que pasar por la dotación objetivo para llegar.
        */}
        <Tabs
          area="admin"
          tabs={[
            {
              id: 'despliegue',
              label: translate(lang, 'admin.tabDeployment'),
              content: (
                <>
            <section className="panel">
              <h2>{translate(lang, 'admin.deployment')}</h2>
              <div className="card">
                <div className="meta">
                  {provider
                    ? translate(lang, 'admin.provider', [provider])
                    : translate(lang, 'admin.noProvider')}
                </div>
                {demo && <div className="meta">{translate(lang, 'admin.demo')}</div>}
                <div className={storage.encrypted ? 'meta' : 'error'}>
                  {translate(lang, `storage.${storage.encryptionAtRest}`)}
                </div>
                {/* Como el cifrado: si no se exige segundo factor, se dice en rojo (CJIS lo exige). */}
                {mfa && (
                  <div className={mfa === 'off' ? 'error' : 'meta'}>{translate(lang, `mfa.${mfa}`)}</div>
                )}
                {license && (
                  <div className="meta">
                    {translate(lang, 'admin.license', [
                      translate(lang, `licenseState.${license.state}`),
                      license.soldTo || '—',
                      license.notAfter ?? '—',
                    ])}
                  </div>
                )}
                {/* Lo más importante de esta pantalla es lo que NO hace. */}
                {deadLetters && deadLetters.count > 0 && (
                  <div className="error">
                    {translate(lang, deadLetters.count === 1 ? 'admin.deadLetters.one' : 'admin.deadLetters', [
                      deadLetters.count, deadLetters.types.join(', '),
                      deadLetters.latest ? fmtDateTime(deadLetters.latest, lang) : '—',
                    ])}
                  </div>
                )}
                {/* Cada uno, y descartarlo una vez revisado: antes solo se contaban, y uno ya
                    analizado dejaba la salud degradada para siempre (H-73). */}
                {deadLetterItems.map((m) => (
                  <DeadLetterRow key={m.id} lang={lang} item={m} busy={busy} onDiscard={onDiscardDeadLetter} />
                ))}
                <div className="meta">{translate(lang, 'admin.usersElsewhere')}</div>
              </div>
            </section>

                </>
              ),
            },
            ...(canReadAgency ? [{
              id: 'agencia',
              label: translate(lang, 'admin.tabAgency'),
              content: (
                <>
            {canReadAgency && (
              <AgencyConfig
              canRules={canRules} canLayers={canLayers}
              shiftTemplates={shiftTemplates} onSaveShiftTemplates={onSaveShiftTemplates}
              districts={districts} onUploadDistricts={onUploadDistricts} onDistrictsLoaded={onDistrictsLoaded}
              zones={zones} onUploadZones={onUploadZones} onZonesLoaded={onZonesLoaded}
              districtVersions={districtVersions} zoneVersions={zoneVersions}
                lang={lang}
                targets={targets}
                jurisdictions={jurisdictions}
                busy={busy}
                onSaveTargets={onSaveTargets}
                onSaveJurisdictions={onSaveJurisdictions}
              />
            )}

            {canReadAgency && (
              <fieldset className="plain" disabled={!canRules}>
              <AgencyRules
                lang={lang}
                deviation={deviation}
                approval={approval}
                busy={busy}
                onSaveDeviation={onSaveDeviation}
                onSaveApproval={onSaveApproval}
                formation={formation}
                shiftCodes={shiftTemplates.map((t) => t.code)}
                onSaveFormation={onSaveFormation}
              />
              </fieldset>
            )}

                </>
              ),
            }] : []),
            // Cómo se presenta la agencia: aparte de su organización, porque lo cambia otra
            // persona (comunicación social) y con otra frecuencia.
            ...(canConfigure && onSaveBranding && onSaveBadge && onRemoveBadge ? [{
              id: 'tema',
              label: translate(lang, 'admin.tabTheme'),
              content: (
                <AgencyTheme lang={lang} marca={branding} busy={busy}
                  onSave={onSaveBranding} onSaveBadge={onSaveBadge} onRemoveBadge={onRemoveBadge} />
              ),
            }] : []),
            ...(canDevices ? [{
              id: 'telefonos',
              label: translate(lang, 'admin.tabDevices'),
              content: (
                <>
            {canDevices && (
              <section className="panel">
                <h2>{translate(lang, 'admin.devices', [devices.length])}</h2>
                <p className="notice">{translate(lang, 'admin.devicesHelp')}</p>

                <div className="card">
                  <div className="actions">
                    <input
                      value={nuevoId}
                      onChange={(e) => setNuevoId(e.target.value.toUpperCase())}
                      placeholder={translate(lang, 'admin.deviceId')}
                      style={{ width: 170 }}
                      aria-label={translate(lang, 'admin.deviceId')}
                    />
                    <input
                      value={nuevaEtiqueta}
                      onChange={(e) => setNuevaEtiqueta(e.target.value)}
                      placeholder={translate(lang, 'admin.deviceLabel')}
                      style={{ width: 220 }}
                      aria-label={translate(lang, 'admin.deviceLabel')}
                    />
                    <button
                      className="primary"
                      disabled={busy || !nuevoId.trim() || !nuevaEtiqueta.trim()}
                      onClick={() => { onRegister(nuevoId.trim(), nuevaEtiqueta.trim()); setNuevoId(''); setNuevaEtiqueta('') }}
                    >
                      {translate(lang, 'admin.register')}
                    </button>
                  </div>
                </div>

                {devices.map((d) => {
                  const revocado = d.revokedAt !== null
                  return (
                    <div key={d.deviceId} className={`card ${revocado ? 'warning' : ''}`}>
                      <div className="row-line">
                        <h3 className="grow">{d.label}</h3>
                        <span className={`chip ${revocado ? 'danger' : 'ok'}`}>
                          {translate(lang, `deviceStatus.${d.status}`)}
                        </span>
                      </div>
                      <div className="meta">{d.deviceId}</div>
                      <div className="meta">{translate(lang, 'admin.registeredAt', [cuando(d.registeredAt)])}</div>
                      {revocado
                        ? <div className="meta">{translate(lang, 'admin.revokedAt', [cuando(d.revokedAt!), d.revokedReason])}</div>
                        : (
                          <div className="actions">
                            <input
                              value={motivos[d.deviceId] ?? ''}
                              onChange={(e) => setMotivos({ ...motivos, [d.deviceId]: e.target.value })}
                              placeholder={translate(lang, 'admin.revokeReason')}
                              style={{ width: 220 }}
                            />
                            <button
                              className="danger"
                              disabled={busy || !(motivos[d.deviceId] ?? '').trim()}
                              onClick={() => onRevoke(d.deviceId, (motivos[d.deviceId] ?? '').trim())}
                            >
                              {translate(lang, 'admin.revoke')}
                            </button>
                          </div>
                        )}
                    </div>
                  )
                })}
              </section>
            )}

                </>
              ),
            }] : []),
            ...(canAudit ? [{
              id: 'registro',
              label: translate(lang, 'admin.tabAudit'),
              content: (
                <>
            {canAudit && (
              <section className="panel">
                <h2>{translate(lang, 'admin.audit')}</h2>
                <p className="notice">{translate(lang, 'admin.auditHelp')}</p>
                <div className="actions">
                  <select value={auditKind} onChange={(e) => onAuditKind(e.target.value)}
                    aria-label={translate(lang, 'admin.auditFilter')}>
                    <option value="">{translate(lang, 'admin.auditAll')}</option>
                    <option value="formation">{translate(lang, 'admin.auditExceptions')}</option>
                    <option value="plan">{translate(lang, 'admin.auditPlans')}</option>
                    <option value="device">{translate(lang, 'admin.auditDevices')}</option>
                    <option value="policy">{translate(lang, 'admin.auditPolicies')}</option>
                    <option value="branding">{translate(lang, 'admin.auditBranding')}</option>
                    <option value="roster">{translate(lang, 'admin.auditRoster')}</option>
                  </select>
                </div>
                <DataNote lang={lang} loaded={loaded} problem={problem} count={audit.length} empty="admin.auditEmpty" />
                {audit.map((e, i) => (
                  <div key={`${e.at}-${i}`} className="card">
                    <div className="row-line">
                      <strong className="grow">{translate(lang, `audit.${e.kind}`)}</strong>
                      <span className="meta">{cuando(e.at)}</span>
                    </div>
                    <div className="meta">
                      {e.subjectCode ? translate(lang, `audit.${e.subjectCode}`) : e.subject}
                      {e.actor && ` · ${translate(lang, 'admin.by', [e.actor])}`}
                    </div>
                    {e.detail && <div className="meta">{systemNote(lang, e.detail)}</div>}
                  </div>
                ))}
              </section>
            )}
                </>
              ),
            }] : []),
          ]}
        />
      </aside>
    </div>
  )
}
